import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useStytchB2BClient, useStytchMemberSession } from '@stytch/react/b2b';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { useTimeRange } from '../contexts/TimeRangeContext';
import {
  TabbedPageLayout,
  AppCard
} from '../components';
import OnboardingModal from '../components/OnboardingModal';
import adminService from '../services/AdminService';
import { components, typography, palette } from '../utils/designTokens';
import { XCircle } from 'lucide-react';
import { useHomeApps } from '../services/homeApi';

const Overview = () => {
  const { displayLabel: timeLabel, selectedRange } = useTimeRange();
  const { selectedEnvironment, fetchEnvironments, environments, environmentsLoaded, environmentsError } = useEnvironment();
  const stytchClient = useStytchB2BClient();
  const { session } = useStytchMemberSession();
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  // Once the user closes the modal, don't pop it back open for the rest of
  // this page instance even if apps/environments re-fetch (e.g. a 401
  // retry) while the tenant is still empty.
  const onboardingDismissedRef = useRef(false);

  // Stable identity for "same logged-in session". The `session` OBJECT gets a
  // new identity on every Stytch heartbeat (~4 min) — keying effects on it (or
  // on the rotating JWT) refetches unchanged data on every heartbeat. The JWT
  // is read fresh at call time instead.
  const sessionId = session?.member_session_id;

  // Fetch environments once per login session (fetchEnvironments reads the
  // current JWT via getTokens() when it runs)
  useEffect(() => {
    if (stytchClient && session) {
      fetchEnvironments(stytchClient, session);
    }
  }, [stytchClient, sessionId, fetchEnvironments]);

  // Fetch user profile to check if admin (for onboarding modal)
  useEffect(() => {
    const checkAdminRole = async () => {
      const jwt = stytchClient?.session.getTokens()?.session_jwt;
      if (!jwt) return;
      try {
        adminService.setAuthToken(jwt);
        const data = await adminService.getOrganization();
        const role = data.profile?.role || '';
        const roles = role.split(',').map(r => r.trim());
        setIsAdmin(roles.includes('stytch_admin'));
      } catch (err) {
        console.error('Failed to check admin role:', err);
        setIsAdmin(false);
      }
    };
    checkAdminRole();
  }, [stytchClient, sessionId]);

  const { apps: rawApps, loading, error } = useHomeApps({ enabled: !!selectedEnvironment });

  const apps = useMemo(() => {
    return rawApps || null;
  }, [rawApps]);

  useEffect(() => {
    // Wait for environments to have actually loaded — it starts as `[]`,
    // indistinguishable from "tenant genuinely has none" until
    // environmentsLoaded flips. Without this gate, the modal can trip on
    // the pre-load empty state and then never re-close, since this effect
    // recomputes the decision fresh each time (no stale latch).
    //
    // A failed fetch (environmentsError) is not evidence of an empty
    // tenant either — it means we don't actually know, so don't show
    // onboarding on a transient /v2/data/services failure.
    if (loading || !isAdmin || !environmentsLoaded || environmentsError || onboardingDismissedRef.current) return;
    const hasEnvironments = environments && environments.length > 0;
    const hasApps = apps && apps.length > 0;
    setShowOnboardingModal(!hasEnvironments && !hasApps);
  }, [loading, apps, environments, isAdmin, environmentsLoaded, environmentsError]);

  return (
    <>
    <TabbedPageLayout
      title="Home"
      tabs={[]}
      activeTab=""
      onTabChange={() => {}}
      loading={loading}
      error={error}
      info={null}
      data={apps}
    >
      {error && (
        <div className="text-danger p-4">
          Error loading overview data: {error}
        </div>
      )}

      {apps && apps.length > 0 && (
        <div className="flex flex-col gap-4">
          {/* Section Header */}
          <div className="flex items-end gap-3 pb-3 border-b" style={{ borderColor: palette.neutrals.borderSubtle }}>
            <h2
              style={{
                color: components.sectionHeader.color,
                fontSize: components.sectionHeader.fontSize,
                fontWeight: components.sectionHeader.fontWeight,
                textTransform: components.sectionHeader.textTransform,
                letterSpacing: components.sectionHeader.letterSpacing,
                margin: 0,
              }}
            >
              Applications
            </h2>
            <span className="text-[11px] text-text-neutral">
              {selectedEnvironment} • {timeLabel}
            </span>
          </div>

          {/* Apps Grid - Compact cards, multiple per row */}
          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            {apps.map((app) => (
              <AppCard
                key={app.app_id}
                app={app}
                environment={selectedEnvironment}
                timeWindow={selectedRange}
              />
            ))}
          </div>
        </div>
      )}

      {/* Apps Error */}
      {error && (
        <div
          className={`${components.card.bg} ${components.card.rounded} ${components.card.padding}`}
          style={{
            border: `1px solid ${palette.status.danger}`,
            borderLeft: `4px solid ${palette.status.danger}`,
            color: palette.text.primary
          }}
        >
          <div className="flex items-start gap-3">
            <XCircle size={20} style={{ color: palette.status.danger }} />
            <div>
              <div className={`${typography.weight.semibold} mb-1`}>Failed to load apps</div>
              <div className={typography.size.sm} style={{ color: palette.text.supporting }}>
                {error}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No apps message */}
      {!error && apps && apps.length === 0 && (
        <div
          className={`${components.card.bg} ${components.card.rounded} ${components.card.padding} text-center`}
          style={{
            border: `1px solid ${components.card.borderColor}`,
            color: palette.text.muted
          }}
        >
          No apps found for this environment
        </div>
      )}
    </TabbedPageLayout>

      {/* Onboarding Modal - shown when user has no traces */}
      <OnboardingModal
        isOpen={showOnboardingModal}
        onClose={() => {
          onboardingDismissedRef.current = true;
          setShowOnboardingModal(false);
        }}
        stytch={stytchClient}
      />
    </>
  );
};

export default Overview;
